import {
  MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME,
  useMessagesContext,
} from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useDockedContext } from 'Context/DockedContext';
import { useEffect } from 'react';

function dockDraft(dockItem, composeDraftMessageResource) {
  if (!composeDraftMessageResource) {
    return;
  }

  dockItem(
    MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME,
    'New Message',
    composeDraftMessageResource.getParentCollection(),
    {
      autoSave: false,
      saved: false,
      displaySavedState: false,
    },
  );
}

const useMessagesPageCompose = (householdId, nodeName) => {
  // Messages Context
  if (!nodeName) {
    throw new Error('No node name provided to useMessagesPageCompose');
  }
  const { isSearching, composeDraftMessageResource, loadOrCreateDraftForCompose } = useMessagesContext();
  const { displayInfo } = useSnackbarContext();
  const { logEvent } = useEventTrackingLogEvent({ nodeName: 'Messages Footer Bar' });
  const { undockItem, dockItem, isItemDocked } = useDockedContext();
  const isDocked = isItemDocked(MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME);

  const undock = async () => {
    if (!isDocked) {
      return;
    }
    await undockItem(MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME);
  };

  const handleCompose = async () => {
    if (isSearching) {
      displayInfo('You may not compose a message while searching is in progress', { timeout: 3000 });
      logEvent(nodeName);
      return;
    }

    // If currentComposeDraftMessageResource is null, load or create one
    const draft = composeDraftMessageResource || await loadOrCreateDraftForCompose(householdId);
    dockDraft(dockItem, draft);
  };

  // The dock context and message context both store the draft message resource if there is one, but auto-saving causes message context to get a new resource created from the server response, while the dock context gets out of date.
  // This useEffect ensures that the dock is updated when message context auto-saves (or more generally, whenever it changes).
  // But we only want to update the dock in this use effect - if the draft changes after the dock was hidden, we shouldn't pop the dock back up again
  useEffect(() => {
    if (isDocked) {
      dockDraft(dockItem, composeDraftMessageResource);
    }
  }, [composeDraftMessageResource]);

  return {
    handleCompose,
    undock,
  };
};

export { useMessagesPageCompose };
